import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface KpiRow {
  readonly kpiId: string;
  readonly kpiCode: string;
  readonly kpiName: string;
  readonly actualValue: number;
  // executive_kpi_cube.target_value / variance_pct are both nullable.
  readonly targetValue: number | null;
  readonly variancePct: number | null;
  readonly trend: string;
}

const TRENDS = ['', 'UP', 'DOWN', 'FLAT'];

/**
 * GR L26.05 — Executive KPI (`/dwh/executive-kpi`). The board-level scorecard
 * sourced from the `executive_kpi_cube`: each KPI's actual vs target, variance
 * and trend direction, so leadership can see performance at a glance.
 * Filterable by trend direction.
 */
export function GrExecutiveKpiPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const [rows, setRows] = useState<KpiRow[]>([]);
  const [trend, setTrend] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (trend) params.set('trend', trend);
    const qs = params.toString();
    apiRequest<{ data: KpiRow[] }>(`/v1/dwh/kpis/executive${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, trend]);

  return (
    <main aria-label="executive-kpi">
      <h1>{tr('gr26.kpi.title')}</h1>
      <div className="filters">
        <label>
          {tr('gr26.kpi.trend')}
          <select aria-label="filter-trend" value={trend} onChange={(e) => setTrend(e.target.value)}>
            {TRENDS.map((t) => <option key={t || 'all'} value={t}>{t || tr('gr26.kpi.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('gr26.kpi.loading')}</p> : (
        <>
          <p aria-label="kpi-count">{tr('gr26.kpi.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{tr('gr26.kpi.empty')}</p> : (
            <table aria-label="kpi-table">
              <thead><tr><th>{tr('gr26.kpi.code')}</th><th>{tr('gr26.kpi.name')}</th><th>{tr('gr26.kpi.actual')}</th><th>{tr('gr26.kpi.target')}</th><th>{tr('gr26.kpi.variance')}</th><th>{tr('gr26.kpi.trendCol')}</th></tr></thead>
              <tbody>
                {rows.map((k) => (
                  <tr key={k.kpiId}>
                    <td>{k.kpiCode}</td><td>{k.kpiName}</td>
                    <td>{number(k.actualValue)}</td><td>{number(k.targetValue)}</td>
                    <td>{k.variancePct == null ? '—' : `${number(k.variancePct, 1)}%`}</td><td aria-label={`trend-${k.kpiId}`}>{k.trend}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
